// Every show this site can serve, one entry each (the "workshop" setup, 2026-10-21):
//   - on the show's production hostname, "/" is its client page and nothing else exists;
//   - on the board (dev.silvr.shop, localhost), "/" lists the shows, "/<key>" previews the client page
//     exactly as production renders it, and "/<key>/curate" is the curation page (a show with
//     `curatedOn` has none: its clips are curated on that show's page).
// A show that needs its own behaviour gets a folder src/shows/<key>/ with a page built from the shared
// components (components/shoppable-video, components/silvr) and points `Page` at it; the default is the
// shared page in page/show.tsx.
import type { ComponentType, LazyExoticComponent } from 'react';
import type { PublishedClip } from '../utils/clipApi';
import type { ConfirmedProduct } from './confirmed';
import { curatePllClip, PLL_CONFIRMED } from './pll/products';
import { curatePllS02E01Clip, PLL_S02E01_CONFIRMED } from './pll/s02e01';

export interface Show {
  /** URL segment on the board: /<key>, /<key>/curate. */
  key: string;
  name: string;
  /** Production hostname that serves this show's client page at "/". Absent on a show that is not
   *  live anywhere (a scene being worked on). */
  host?: string;
  /** Only the local board (localhost, local.silvr.shop) lists and serves this show: a scene being
   *  tried out that must not appear on dev.silvr.shop or any production host (Felipe, 2026-10-22). */
  localOnly?: boolean;
  /** How a viewer gets past the gate on this show's production host (SIL-1595): 'email' asks for a
   *  work email, which the API accepts when its domain is on the site's allowed list (viewer_sites /
   *  viewer_rules, edited from the board's Viewers panel) and not blocked; absent = the shared viewer
   *  passcode, as on the board itself. The API decides the mode for real from the request's Origin;
   *  this picks the form the gate shows first, and GET /viewer-pass corrects it if they disagree. */
  signIn?: 'email' | 'passcode';
  /** Published clip slugs (the ML pipeline's); the client page plays the first. */
  slugs: string[];
  /** Header text on the client page. */
  title: string;
  subtitle: string;
  /** Logo in public/, shown in the client header (inverted to white on the dark stage). */
  logo: string;
  logoAlt: string;
  /** Optional show-specific client page (src/shows/<key>/); the shared page otherwise. */
  Page?: LazyExoticComponent<ComponentType<{ show: Show }>>;
  /** Optional last word on what the client sees: products a person confirmed for this show's
   *  garments (src/shows/<key>/products.ts) replace the catalog match on the published clip. */
  curate?: (clip: PublishedClip) => PublishedClip;
  /** The same products, for the curation page to show next to the catalog candidates. */
  confirmed?: ConfirmedProduct[];
  /** Key of the show whose Curate page edits this show's clips, for a show that plays another show's
   *  clips with a different player. It gets no Curate page of its own (no Curate on its board tile, and
   *  /<key>/curate goes to that show's), so a clip is curated and published from one place: a second
   *  door looked like a sandbox but published to the same production host (Felipe's review of #120;
   *  Jason, 2026-10-26). */
  curatedOn?: string;
  /** Which shopping surface the client page plays the clip with: the "+" with a hover card and a white
   *  drawer (the default), or 'tv', the "Shop by TV" design (a gold chip and a dark side sheet). */
  player?: 'tv';
  /** Short episode label over the picture in the 'tv' player, e.g. "S2 E7". */
  episode?: string;
}

export const SHOWS: Show[] = [
  {
    key: 'pll',
    name: 'Pretty Little Liars: Original Sin',
    host: 'pll.silvr.shop',
    // HBO people and Silvr's own aliases sign in with their work email (Jason, 2026-10-23); the allowed
    // domains (wbd.com, hbo.com, max.com, warnerbros.com, warnermedia.com, silvr.ai) are the API's, not here.
    signIn: 'email',
    // The demo plays the 30-second cut (the Faran/Kelly scene, frames 0-719 of the minute); the full
    // minute stays listed for curation (Felipe, 2026-10-22).
    slugs: ['pll-s02e07-1110-1140', 'pll-s02e07-1110-1210'],
    title: 'Pretty Little Liars: Original Sin',
    subtitle: 'Season 2 · Episode 7',
    // The client page plays the "Shop by TV" design (Paula, 2026-10-26), the one /pll-tv tried out.
    episode: 'S2 E7',
    player: 'tv',
    logo: '/hbo-logo.svg',
    logoAlt: 'HBO',
    curate: curatePllClip,
    confirmed: PLL_CONFIRMED,
  },
  {
    // The same PLL scene with the "Shop by TV" design (Paula's mockups, 2026-10-24): a gold "Shop this
    // scene" chip with the Silvr mark, a "+" on the garments and a dark side sheet listing what is in
    // the frame, each garment out to its store. On the board (dev.silvr.shop and the local board) beside
    // /pll for comparison, and on no production host: it has none (Jason, 2026-10-25); same clip, same
    // confirmed products. Curated on /pll only: a Curate page here would publish the same clips, and so
    // change pll.silvr.shop (Jason, 2026-10-26).
    key: 'pll-tv',
    name: 'Pretty Little Liars: Original Sin · Shop by TV',
    slugs: ['pll-s02e07-1110-1140', 'pll-s02e07-1110-1210'],
    title: 'Pretty Little Liars: Original Sin',
    subtitle: 'Season 2 · Episode 7',
    episode: 'S2 E7',
    logo: '/hbo-logo.svg',
    logoAlt: 'HBO',
    player: 'tv',
    curate: curatePllClip,
    curatedOn: 'pll',
  },
  {
    // A second scene, tried out on the local board only (Felipe, 2026-10-22): S02E01, 21:42-22:00, the
    // cemetery. 18 s, the last of the 21:30-22:00 scene ("cut from second 0 to 12 ... leave 12 to 30").
    // Not on dev.silvr.shop, not on any production host, until it is promoted into the show above.
    //
    // The full 21:30-22:00 scene is deliberately NOT listed: these confirmed frame ranges are the cut's
    // (the scene's minus 288), so on that slug they would land 12 s early, on the Orpheum beat
    // (jaysilvr's review of #108). It is still curatable by its own slug on the box.
    key: 'pll-s02e01',
    name: 'Pretty Little Liars: Original Sin · S02E01 scene (local)',
    slugs: ['pll-s02e01-2142-2200'],
    title: 'Pretty Little Liars: Original Sin',
    subtitle: 'Season 2 · Episode 1',
    logo: '/hbo-logo.svg',
    logoAlt: 'HBO',
    localOnly: true,
    curate: curatePllS02E01Clip,
    confirmed: PLL_S02E01_CONFIRMED,
  },
];

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]', 'local.silvr.shop'];

export function isLocalHost(host: string = window.location.hostname): boolean {
  return LOCAL_HOSTS.includes(host.toLowerCase());
}

/** The shows this host may list and serve: a localOnly show exists on the local board and nowhere else. */
export function visibleShows(host: string = window.location.hostname): Show[] {
  return SHOWS.filter((show) => !show.localOnly || isLocalHost(host));
}

export function showByKey(key: string | undefined): Show | undefined {
  return visibleShows().find((show) => show.key === key);
}

export function showByHost(host: string): Show | undefined {
  const h = host.toLowerCase();
  return SHOWS.find((show) => show.host && show.host.toLowerCase() === h);
}

// Hostnames that serve the board. VITE_APP_BOARD_HOSTS overrides (comma-separated); localhost and
// 127.0.0.1 are always boards so local work needs no configuration.
const BOARD_HOSTS = (import.meta.env.VITE_APP_BOARD_HOSTS || 'dev.silvr.shop,local.silvr.shop')
  .split(',')
  .map((h: string) => h.trim().toLowerCase())
  .filter(Boolean);

export type HostMode = { mode: 'board' } | { mode: 'show'; show: Show } | { mode: 'none' };

export function resolveHost(host: string = window.location.hostname): HostMode {
  const h = host.toLowerCase();
  const show = showByHost(h);
  if (show) return { mode: 'show', show };
  if (BOARD_HOSTS.includes(h) || h === 'localhost' || h === '127.0.0.1' || h === '[::1]') return { mode: 'board' };
  return { mode: 'none' };
}
